import type { ReactNode } from 'react'

/** Position eines Lagerplatzes in der Regalfront: Feld × Platz × Ebene. */
export interface RegalPos { field: number; slot: number; level: number }

/** Eine Spalte der Front = ein Platz in einem Feld. Die Spalten kommen in Anzeige-Reihenfolge
 *  (Felder × Plätze nebeneinander) und duerfen lueckenhaft sein — daher keine fields × slots-Matrix. */
export interface RegalColumn { field: number; slot: number; label: string }

export interface RegalFrontProps<T> {
  columns: RegalColumn[]
  levels: number[]
  cellAt(pos: RegalPos): T | undefined
  /** Zell-Darstellung je Position — auch fuer Positionen ohne Platz (cell === undefined). */
  renderCell(cell: T | undefined, pos: RegalPos): ReactNode
  onCellClick?(cell: T | undefined, pos: RegalPos): void
  ariaLabel?: string
}

/** Frontansicht eines Regals (aus der Digitalen Regal-Kontrolle der Erstaufnahme ausgelagert).
 *  Ebenen werden unten → oben gezeichnet: EB1 ist die unterste Zeile. */
export default function RegalFront<T>({ columns, levels, cellAt, renderCell, onCellClick, ariaLabel }: RegalFrontProps<T>) {
  const rows = [...levels].sort((a, b) => b - a)
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse" aria-label={ariaLabel}>
        <thead>
          <tr>
            <th className="p-1 text-[10px] sticky left-0 bg-panel z-10">Ebene</th>
            {columns.map((c) => (
              <th key={c.label} className="p-1 text-[9px] font-mono whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((level) => (
            <tr key={level}>
              <td className="p-1 text-[10px] font-mono font-semibold sticky left-0 bg-panel z-10">EB{level}</td>
              {columns.map((c) => {
                const pos = { field: c.field, slot: c.slot, level }
                const cell = cellAt(pos)
                return (
                  <td key={c.label} className="p-0.5" onClick={onCellClick ? () => onCellClick(cell, pos) : undefined}>
                    {renderCell(cell, pos)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
